// src/components/plan/AssignmentRegistModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Calendar, Clock, Building2, Loader2, ChevronDown, Search } from 'lucide-react';

export default function AssignmentRegistModal({ isOpen, onClose, onSave, isSaving, staffList = [], bizList = [] }) {
  const initialFormState = {
    bizMgtNo: '',       // 사업장관리번호 (select로 받음)
    visitDate: '',      // 방문일자
    visitTime: '',      // 방문시간
    visitRound: 1,      // 방문회차 (기본값 1)
    staffId1: '',       // 수행요원 1
    staffId2: '',       // 수행요원 2 (선택)
    assignStatus: '배정완료', // 진행상태
    remarks: ''         // 특이사항
  };

  const [formData, setFormData] = useState(initialFormState);

  // 🌟 (추가) 사업장 검색 필터링을 위한 상태
  const [searchTerm, setSearchTerm] = useState(''); // 사용자가 입력한 검색어
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // 드롭다운 열림/닫힘 상태
  const dropdownRef = useRef(null); // 외부 클릭 감지용 Ref

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // 🌟 담당자 1을 바꿨는데, 그 사람이 이미 담당자 2로 지정되어 있다면 담당자 2를 초기화
      if (name === 'staffId1' && value === prev.staffId2) {
        newData.staffId2 = '';
      }
      return newData;
    });
  };

  const handleBizSelect = (biz) => {
    setFormData(prev => ({ ...prev, bizMgtNo: biz.bizMgtNo }));
    setSearchTerm(`${biz.bizMgtNo} (${biz.bizName})`); // 선택된 값을 input에 표시
    setIsDropdownOpen(false); // 드롭다운 닫기
  };

  const handleClose = () => {
    setFormData(initialFormState);
    setSearchTerm(''); // 검색어 초기화
    setIsDropdownOpen(false);
    onClose();
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bizMgtNo || !formData.visitDate || !formData.staffId1) {
      alert("필수 항목을 모두 입력해주세요.");
      return;
    }
    // 🌟 핵심 수정: 빈 문자열 처리
    const payload = { ...formData };

    // visitTime이 빈 문자열이면 null로 처리하여 MariaDB의 TIME 타입 에러 방지
    if (payload.visitTime === '') {
      payload.visitTime = null;
    }

    // 다른 선택(Optional) 값들도 빈 문자열이면 null 처리 (안전성 강화)
    if (payload.staffId2 === '') payload.staffId2 = null;
    if (payload.remarks === '') payload.remarks = null;

    // 회차를 숫자로 변환해서 전송
    payload.visitRound = parseInt(payload.visitRound, 10);

    try {
      await onSave(payload);

      // 저장 성공 시에만 폼 초기화
      setFormData(initialFormState);
      setSearchTerm('');
    } catch (error) {
      // API 실패 시 브라우저 콘솔에 빨간 에러가 뜨는 것을 막아줍니다.
      // alert 팝업은 이미 usePlanAssignPage.js의 onError에서 띄우고 있으므로 
      // 여기서는 그냥 조용히 넘어가도록(catch) 둡니다.
      console.log("등록 실패: 백엔드 검증 로직에 의해 차단됨");
    }
  };

  const filteredBizList = bizList.filter(b => {
    if (!searchTerm) return true;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return b.bizName?.toLowerCase().includes(lowerSearchTerm) || b.bizMgtNo?.toLowerCase().includes(lowerSearchTerm);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200">

        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            신규 배정 등록
          </h3>
          <button onClick={handleClose} disabled={isSaving} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-[2fr_1fr] gap-4">
              {/* 🌟 수정됨: 검색 가능한 사업장 선택 UI (Custom Select) */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">사업장 검색 및 선택 <span className="text-red-500">*</span></label>
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      placeholder="사업장명 또는 관리번호 검색"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setFormData(prev => ({ ...prev, bizMgtNo: '' })); // 타이핑을 시작하면 선택된 값 초기화
                        setIsDropdownOpen(true); // 입력 시 드롭다운 열기
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                    />
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>

                  {/* 드롭다운 리스트 */}
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg custom-scrollbar">
                      {filteredBizList.length > 0 ? (
                        <ul className="py-1">
                          {filteredBizList.map((b) => (
                            <li
                              key={b.bizMgtNo}
                              onClick={() => handleBizSelect(b)}
                              className="cursor-pointer px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 transition-colors"
                            >
                              <div className="font-semibold text-slate-900">{b.bizName}</div>
                              <div className="text-xs text-slate-500">{b.bizMgtNo}</div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="p-4 text-center text-sm text-slate-500">검색 결과가 없습니다.</div>
                      )}
                    </div>
                  )}
                </div>
                {/* 시각적으로 필수 선택임을 알리는 숨겨진 input (폼 검증용) */}
                <input type="hidden" name="bizMgtNo" value={formData.bizMgtNo} required />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">방문 회차 <span className="text-red-500">*</span></label>
                <input type="number" name="visitRound" value={formData.visitRound} onChange={handleChange} min="1" max="5" required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> 방문 일자</label>
                <input type="date" name="visitDate" value={formData.visitDate} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> 방문 시간</label>
                <input type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>

            {/* 담당자 선택 등 기존 로직 그대로 유지 */}
            <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">담당자 1 (정) <span className="text-red-500">*</span></label>
                <div className="relative">
                  <select name="staffId1" value={formData.staffId1} onChange={handleChange} required className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white">
                    <option value="">수행요원 선택</option>
                    {staffList.map((m) => <option key={m.staffId} value={m.staffId}>{m.staffNm} ({m.staffId})</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">담당자 2 (부)</label>
                <div className="relative">
                  <select name="staffId2" value={formData.staffId2} onChange={handleChange} className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white">
                    <option value="">선택 안함</option>
                    {staffList
                      .filter(m => m.staffId !== formData.staffId1)
                      .map((m) => <option key={m.staffId} value={m.staffId}>{m.staffNm} ({m.staffId})</option>)
                    }
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">진행 상태</label>
                <select name="assignStatus" value={formData.assignStatus} onChange={handleChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white">
                  <option value="배정완료">배정완료</option>
                  <option value="방문예정">방문예정</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">특이사항/비고</label>
              <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"></textarea>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 rounded-b-2xl bg-slate-50 px-6 py-4 border-t border-slate-100">
            <button type="button" onClick={handleClose} disabled={isSaving} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors">
              취소
            </button>
            <button type="submit" disabled={isSaving} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isSaving ? '저장 중...' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}